import React from "react";
import { useTranslation } from "react-i18next";
import type { MmEvent } from "../../api/mentalModels";

/**
 * Object biography: per-attribute sparklines + operator timeline.
 * Sparklines are plain SVG (no chart.js dependency for four tiny lines).
 */

const SPARK_ATTRS: Array<{ key: string; min: number; max: number }> = [
  { key: "charge", min: -5, max: 5 },
  { key: "resolution", min: 1, max: 5 },
  { key: "certainty", min: 1, max: 5 },
  { key: "identification", min: 1, max: 5 },
];

const Sparkline: React.FC<{
  points: Array<{ t: string; v: number }>;
  min: number;
  max: number;
}> = ({ points, min, max }) => {
  const w = 120;
  const h = 28;
  if (points.length < 2) {
    return <span className="text-xs text-muted-foreground">–</span>;
  }
  const xs = points.map((_, i) => (i / (points.length - 1)) * (w - 4) + 2);
  const ys = points.map((p) => h - 3 - ((p.v - min) / (max - min)) * (h - 6));
  const d = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  const lastUp = points[points.length - 1].v >= points[0].v;
  return (
    <svg width={w} height={h} className="overflow-visible">
      <path d={d} fill="none" strokeWidth={1.5} className={lastUp ? "stroke-emerald-500" : "stroke-primary"} />
      <circle cx={xs[xs.length - 1]} cy={ys[ys.length - 1]} r={2.5} className={lastUp ? "fill-emerald-500" : "fill-primary"} />
    </svg>
  );
};

/** Renders the substantive content of an applied operator (QA: the log must
 * show WHAT was done, not just which operator and when). */
const OperatorContent: React.FC<{ payload: any }> = ({ payload }) => {
  const out = payload.output || {};
  const lines: React.ReactNode[] = [];

  if (out.chosenName) lines.push(<span key="n">“{out.chosenName}”</span>);
  if (out.chosenFrame)
    lines.push(
      <span key="f">
        <b>{out.chosenFrame}</b>
        {out.statement && <> — {out.statement}</>}
      </span>
    );
  if (out.response) lines.push(<span key="r">{out.response}</span>);
  if (Array.isArray(out.members) && out.members.length)
    lines.push(
      <ul key="m" className="list-disc ps-4 space-y-0.5">
        {out.members.map((m: any, i: number) => (
          <li key={i}>{m.statement}</li>
        ))}
      </ul>
    );
  if (out.probe)
    lines.push(
      <span key="p">
        {out.probe}
        {out.loseCondition && (
          <span className="text-amber-700"> — loses if: {out.loseCondition}</span>
        )}
      </span>
    );
  if (out.inside || out.outside)
    lines.push(
      <span key="b">
        {Array.isArray(out.inside) && <>in: {out.inside.join(", ")}. </>}
        {Array.isArray(out.outside) && <>out: {out.outside.join(", ")}</>}
      </span>
    );

  // ---- design family outputs (ICDF) ----
  if (out.problem || out.doneMeans)
    lines.push(
      <span key="def">
        {out.problem && (
          <>
            <b>{out.problem}</b>
            {out.doneMeans && " — "}
          </>
        )}
        {out.doneMeans && <>done means: {out.doneMeans}</>}
      </span>
    );
  const bulletList = (key: string, items: any[], render: (x: any) => React.ReactNode) => (
    <ul key={key} className="list-disc ps-4 space-y-0.5">
      {items.map((x, i) => (
        <li key={i}>{render(x)}</li>
      ))}
    </ul>
  );
  if (Array.isArray(out.criteria) && out.criteria.length && !out.scores)
    lines.push(bulletList("crit", out.criteria, (c) => c.name || c));
  if (Array.isArray(out.options) && out.options.length && !out.synthesis)
    lines.push(
      bulletList("opt", out.options, (o) =>
        o.name ? (
          <>
            <b>{o.name}</b>
            {o.sketch && <> — {o.sketch}</>}
          </>
        ) : (
          o
        )
      )
    );
  if (Array.isArray(out.findings) && out.findings.length)
    lines.push(
      bulletList("find", out.findings, (f) => (
        <>
          {f.question && <i>{f.question}: </i>}
          {f.answer}
        </>
      ))
    );
  if (Array.isArray(out.creaks) && out.creaks.length)
    lines.push(
      bulletList("crk", out.creaks, (c) => (
        <>
          {c.scene && <i>{c.scene}: </i>}
          {c.creak || c}
        </>
      ))
    );
  if (Array.isArray(out.patches) && out.patches.length)
    lines.push(
      bulletList("pat", out.patches, (p) => (
        <>
          {p.weakPoint && <i>{p.weakPoint} → </i>}
          {p.patch || p}
        </>
      ))
    );
  if (Array.isArray(out.causes) && out.causes.length)
    lines.push(bulletList("cau", out.causes, (c) => c.cause || c));
  if (out.synthesis) lines.push(<span key="syn">{out.synthesis}</span>);
  if (out.firstStep)
    lines.push(
      <span key="fs">
        {out.firstStep}
        {out.when && <span className="text-muted-foreground"> ({out.when})</span>}
      </span>
    );

  if (!lines.length) return null;
  return <div className="mt-1 text-foreground/80 space-y-0.5">{lines}</div>;
};

interface Props {
  events: MmEvent[];
  series: Record<string, Array<{ t: string; v: number }>>;
}

const MmBiography: React.FC<Props> = ({ events, series }) => {
  const { t, i18n } = useTranslation();
  const operatorEvents = events.filter(
    (e) =>
      e.kind === "operator" ||
      e.kind === "status" ||
      e.kind === "emit" ||
      e.kind === "goal" ||
      e.kind === "visit"
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {SPARK_ATTRS.map(({ key, min, max }) => {
          const pts = series[key] || [];
          const first = pts[0]?.v;
          const last = pts[pts.length - 1]?.v;
          return (
            <div key={key} className="rounded-xl border border-border/70 bg-white p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium capitalize">{t(`mm_attr_${key}_short`, key)}</span>
                {first !== undefined && last !== undefined && (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {first} → {last}
                  </span>
                )}
              </div>
              <Sparkline points={pts} min={min} max={max} />
            </div>
          );
        })}
      </div>

      <div className="space-y-2">
        {operatorEvents
          .slice()
          .reverse()
          .map((e) => (
            <div
              key={e._id}
              className="relative rounded-xl border border-border/60 bg-white p-3 ps-4 text-xs before:absolute before:inset-y-3 before:start-0 before:w-0.5 before:rounded-full before:bg-primary/20"
            >
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="tabular-nums whitespace-nowrap">
                  {new Date(e.createdOn).toLocaleDateString(i18n.language)}
                </span>
                {e.kind === "operator" && (
                  <b className="text-foreground">
                    {t(`mm_op_${e.payload.operatorId}_past`, e.payload.operatorId)}
                  </b>
                )}
                {e.kind === "status" && (
                  <span>
                    {t("mm_status_changed", "status")}: {e.payload.from} → {e.payload.to}
                    {e.payload.retiredNote && <> — {e.payload.retiredNote}</>}
                    {e.payload.parkedCondition && <> ({e.payload.parkedCondition})</>}
                  </span>
                )}
                {e.kind === "emit" && (
                  <span>
                    {e.payload.artifactType === "insight"
                      ? t("mm_emitted_insight", "saved an insight")
                      : t("mm_emitted_action", "created an action item")}
                    {e.payload.content && <> — {e.payload.content}</>}
                  </span>
                )}
                {e.kind === "goal" && (
                  <span>
                    <b className="text-foreground">{t("mm_goal_set", "Set a goal")}</b>
                    {e.payload.statedOutcome && <> — “{e.payload.statedOutcome}”</>}
                  </span>
                )}
                {e.kind === "visit" && e.payload.reason && (
                  <span className="flex items-center gap-1.5 flex-wrap">
                    <span
                      className={
                        e.payload.reason.kind === "friction"
                          ? "rounded-full bg-rose-50 text-rose-600 px-2 py-0.5 text-[10px] uppercase tracking-wide"
                          : "rounded-full bg-sky-50 text-sky-600 px-2 py-0.5 text-[10px] uppercase tracking-wide"
                      }
                    >
                      {e.payload.reason.kind === "friction"
                        ? e.payload.reason.frictionType === "internal"
                          ? t("mm_visit_friction_internal", "came up from within")
                          : t("mm_visit_friction_external", "something happened")
                        : t("mm_visit_plan", "working a plan")}
                    </span>
                    {e.payload.reason.note && <span>{e.payload.reason.note}</span>}
                  </span>
                )}
              </div>
              {e.kind === "operator" && <OperatorContent payload={e.payload} />}
            </div>
          ))}
      </div>
    </div>
  );
};

export default MmBiography;
